/**
 * The staffing model's vocabulary.
 *
 * Everything here is plain data. The engine performs no I/O and knows nothing
 * about Postgres, HTTP, or React — its callers (customer-management-api for
 * authoritative snapshots, apps/web for live re-tiering as a threshold moves)
 * hand it accounts and config and get portfolios back.
 */

/**
 * One customer, as the staffing model sees it.
 *
 * `id` is the STABLE key and the only thing the engine matches on — pins,
 * portfolios and coverage are all keyed by it. In production that is
 * `crm.customer_settings.customer_id`; the parity fixture uses the account's
 * display label because the reference implementation keyed on the name. Never
 * key on `name`: display names change, and a name-keyed join is exactly what
 * silently emptied an advisor's book in the CRM (the 2026-08-10 stable-identity
 * work).
 */
export interface Account {
  id: string;
  name: string;
  /** Trailing-12-month BOOKING VOLUME in $M — not revenue to Andavo. */
  spendM: number;
  /**
   * The CSM who owns this account today, per the source system. May be a
   * SENTINEL (see SENTINEL_CSMS) meaning "not really owned by a person", or
   * null. Drives the keep-vs-move logic; it is not the assignment result.
   */
  currentCsm: string | null;
}

/**
 * A service tier. Tiers are DATA, not an enum — the prototype hardcoded three
 * (`ent`/`med`/`sml`) and keyed portfolios by CSM name alone, which is the
 * accident that made "a CSM belongs to exactly one tier" a rule and blocked
 * adding a Micro tier. Here a tier is a config row and portfolios are keyed by
 * (csm, tier), so adding one is an edit rather than a redesign.
 */
export interface TierDef {
  key: string;
  label: string;
  /**
   * Inclusive lower bound in $M. The lowest BAND tier should use -Infinity: zero
   * and NEGATIVE spend are real data. A `named` tier uses +Infinity — see
   * `selection`.
   */
  minSpendM: number;
  /** Service-time target used for the FTE math. */
  hoursPerAccountPerMonth: number;
  /**
   * How accounts get into this tier. Absent means 'band'.
   *
   * WHY THIS EXISTS AS A DISCRIMINATOR rather than a special threshold value.
   * "This tier has no floor" had two OPPOSITE readings the moment hand-picked
   * tiers became possible:
   *
   *   • the catch-all, which takes everything below the last floor, and
   *   • a named tier, which takes nothing at all by spend.
   *
   * Both would be "no floor". Encoding them as the same thing and inferring the
   * difference from position is how a strategic tier ends up quietly absorbing
   * every credit and adjustment in the book. So the two are different KINDS.
   *
   *   'band'  — membership is computed from spend. Exactly one band tier is the
   *             catch-all (minSpendM -Infinity) and it must be the last one.
   *   'named' — membership is BY HAND ONLY, via TierPins. Strategic accounts are
   *             not a size, they are a decision, and they come in every size.
   *             Its minSpendM is +Infinity so that even if a caller walked the
   *             thresholds directly, no spend could ever reach it.
   */
  selection?: 'band' | 'named';
}

/**
 * Account id → the tier key it has been placed in BY HAND, overriding spend.
 *
 * The tier half of an override; `Pins` is the CSM half. They are separate
 * parameters because they answer separate questions — which band of service
 * this account gets, and who holds it — and an account can need either, both,
 * or neither. Conflating them would make "move this strategic account to Dev"
 * impossible to express without also restating its tier.
 *
 * An assignment BEATS the band, whatever kind the target tier is. Forcing a
 * $200K account into Enterprise is a legitimate (and auditable) thing to want,
 * so this is not restricted to `named` tiers.
 */
export type TierPins = Record<string, string>;

/**
 * How a CSM is identified to the engine.
 *
 * OPAQUE. The engine compares these for equality and does nothing else with
 * them — it never parses, splits or displays one. The parity fixture happens to
 * use display names because the prototype did, but that is a property of the
 * fixture, NOT a contract.
 *
 * The phase-2 DB seam must pass a STABLE id (`csm_external_id`, falling back to
 * `csm_email`) and resolve display names for presentation separately. Handing
 * this display names would rebuild the exact failure the CRM's 2026-08-10
 * stable-advisor-identity work exists to prevent: `"Taylor R. Daily"` and
 * `"Taylor Daily"` compare unequal and an advisor's book silently empties.
 */
export type CsmId = string;

export interface StaffingConfig {
  /** Ordered HIGHEST minSpendM first. Validated — an out-of-order list silently mis-tiers. */
  tiers: readonly TierDef[];
  /** Which CSMs staff each tier, by tier key. Order matters: ties in the capacity search go to the first slot. */
  slots: Readonly<Record<string, readonly CsmId[]>>;
  /**
   * Every CSM this configuration may assign work to. REQUIRED, and validated.
   *
   * Slots alone cannot serve as the allow-list, because a legitimate pin target
   * may hold no slot — the manager is exactly that. Without a declared roster
   * the engine accepted ANY pin value and minted a portfolio for it, so a stale
   * or malformed pin (`{customerId: 'attacker-id'}`) silently became a
   * customer's owner with no error anywhere. An assignment engine should not be
   * able to invent a person.
   */
  roster: readonly CsmId[];
  /** Productive hours per FTE per year. The prototype's constant is 2000. */
  hrsPerFteYear: number;
  /**
   * What the distributor equalizes across a tier's slots.
   *
   * `'count'` (the default, and the reference implementation's only behaviour)
   * gives everyone a similar NUMBER of customers. `'spend'` gives everyone a
   * similar amount of BOOKING VOLUME, which is a different and often better
   * question: within a tier, twelve $6M customers is not the same job as twelve
   * $40M ones, and the count-balanced answer says it is.
   *
   * OPTIONAL, defaulting to `'count'`, so every existing caller and the parity
   * goldens are unaffected — the reference had no such concept and the goldens
   * must keep reproducing it exactly.
   *
   * cm-api has stored and validated this field on every config version since the
   * persistence layer shipped. Until this existed the engine ignored it, so a
   * manager could select spend-balancing, have it accepted, stored immutably and
   * read back correctly, and get a count-balanced book anyway.
   */
  balanceBy?: BalanceBy;
}

/** See `StaffingConfig.balanceBy`. */
export type BalanceBy = 'count' | 'spend';

/** accountId → CsmId. Overrides distribution and is tier-independent. */
export type Pins = Readonly<Record<string, CsmId>>;

/**
 * One CSM's book within one tier.
 *
 * `tier` is null for a portfolio that exists ONLY because accounts are pinned to
 * someone who holds no slot — the manager, typically. The reference
 * implementation had no such concept, which is why 23 accounts fell out of its
 * totals; see computePortfolios().
 */
export interface Portfolio {
  csm: CsmId;
  tier: string | null;
  /** Distributed to this CSM by the balancer (excludes pins). */
  assigned: Account[];
  /**
   * Pinned to this CSM, from ANY tier.
   *
   * A CSM's pinned book is a property of the PERSON, not of a tier, so it is
   * attached to exactly ONE of their portfolios — the highest tier they staff.
   * Their other tier portfolios carry an empty list. Attaching it to each one
   * (the first version) made a multi-tier CSM's booking volume and FTE load
   * double-count the whole pinned book, invisibly: `computeCoverage` ignores
   * duplicates within a single CSM, so coverage still read complete.
   */
  pinned: Account[];
  /** Already theirs at this tier and staying. */
  keeping: Account[];
  /** Arriving at this tier from someone else (or newly created). */
  incoming: Account[];
  /** Theirs at this tier today, leaving for another CSM. */
  losing: Account[];
  /** Theirs today in OTHER tiers, reassigning away, keyed by that tier. */
  otherOut: Record<string, Account[]>;
}

export interface AssignmentCoverage {
  total: number;
  assigned: number;
  /** Account ids in no portfolio at all. MUST be empty — see computePortfolios(). */
  unassigned: string[];
  /** Account ids appearing in more than one CSM's book. MUST be empty. */
  doubleAssigned: string[];
}

export interface StaffingResult {
  /** Accounts bucketed by tier key, recomputed from live spend. */
  byTier: Record<string, Account[]>;
  portfolios: Portfolio[];
  coverage: AssignmentCoverage;
  /**
   * Accounts in a tier that has NO CSM slots, keyed by tier.
   *
   * A named, typed field rather than silence. A tier can legitimately be
   * unstaffed — the proposed Micro tier is exactly that, and so is a tier
   * awaiting a new hire — but the first version simply `continue`d past it, so
   * its entire book vanished from every portfolio and the only evidence was a
   * non-empty `coverage.unassigned` that a caller could forget to read. A
   * planning tool that quietly omits 541 accounts from a total is worse than one
   * that refuses to compute.
   *
   * Empty object = every tier is staffed. Callers rendering totals MUST account
   * for this (show it as an unstaffed book, or refuse to publish the number).
   */
  unallocated: Record<string, Account[]>;
}

/**
 * `currentCsm` values that are placeholders, not people.
 *
 * Load-bearing, not an edge case: `Client Services` alone owns 136 of the 704
 * accounts in the reference snapshot. balancedAssign LOCKS an account to its
 * current CSM when that CSM staffs the tier; these three values are excluded
 * from that lock, so their accounts stay distributable.
 */
export const SENTINEL_CSMS: ReadonlySet<string> = new Set([
  'New',
  'Unassigned',
  'Client Services',
]);
